// defs
import { POLLING_TXN_STATUS } from "@payment-client/interfaces/PaymentApi";
import { ConfirmOrderParams } from "@payment-client/apis/Order";
import { ApiResponse } from "@payment-client/interfaces/Api";
import {
    ConfirmPennyTransactionResponse,
    CreatePennyTransactionResponse,
    OrderApiData,
    OrderData,
} from "@swiggy-private/payment-types";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getConfirmOrderParams } from "@payment-client/helpers/OrderHelper";

// apis
import { checkTransactionStatusPublic, confirmPennyTransactionPublic } from "@payment-client/apis/publicPayment";
import { confirmOrderPublic } from "@payment-client/apis/publicOrder";

// constants
import { recordNREvent, NEWRELIC_TABLES, WP_PENNY_TXN_CONFIRM_ORDER_STATE } from "@swiggy-private/checkout-core";

interface CheckTxnStatusProps {
    duration: number;
    delay: number;
    startTime?: number;
    paymentCode: string;
}

export class PollingEnabledPaymentFlow {
    private _running = false;
    private _apiHost: string;
    private _orderData: OrderData | null;
    private _transactionData: CreatePennyTransactionResponse | ConfirmPennyTransactionResponse | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _confirmOrderPromise: Promise<ApiResponse<OrderApiData>> | null = null;
    private _confirmPennyTransactionPromise: Promise<ApiResponse<ConfirmPennyTransactionResponse>> | null = null;

    private static _instance: PollingEnabledPaymentFlow | null = null;

    constructor(
        apiHost: string,
        orderData: OrderData | null,
        transactionData: CreatePennyTransactionResponse | ConfirmPennyTransactionResponse | null = null,
    ) {
        this._apiHost = apiHost;
        this._orderData = orderData;
        this._transactionData = transactionData;
    }

    stop(): void {
        this._running = false;
        PollingEnabledPaymentFlow._instance = null;
    }

    isRunning(): boolean {
        return this._running;
    }

    static getInstance(): PollingEnabledPaymentFlow | null {
        return PollingEnabledPaymentFlow._instance;
    }

    async confirmOrder(): Promise<void> {
        if (!this._running || this._confirmOrderPromise != null || this._orderData == null) return;

        const confirmOrderParams: ConfirmOrderParams = getConfirmOrderParams(this._orderData);
        this._confirmOrderPromise = confirmOrderPublic(this._apiHost, confirmOrderParams);
    }

    async confirmPennyTransaction(): Promise<void> {
        if (!this._running || this._confirmPennyTransactionPromise != null || this._transactionData == null) return;

        // (3.7) Confirm penny transaction triggered — combined with success/failed gives the funnel.
        recordNREvent({
            eventType: NEWRELIC_TABLES.WP_PENNY_TXN_CONFIRM_ORDER,
            state: WP_PENNY_TXN_CONFIRM_ORDER_STATE.CONFIRM_PENNY_TXN_REQUEST,
            orderId: this.getOrderId(),
            paymentTxnId: this._transactionData.paymentTxnId,
        });
        this._confirmPennyTransactionPromise = confirmPennyTransactionPublic(this._apiHost, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            order_id: this._transactionData.paymentTxnId,
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfirmOrderPromise(): Promise<any> | null {
        return this._confirmOrderPromise;
    }

    getConfirmPennyTransactionPromise(): Promise<ApiResponse<ConfirmPennyTransactionResponse>> | null {
        return this._confirmPennyTransactionPromise;
    }

    async checkTxnStatus(props: CheckTxnStatusProps): Promise<boolean> {
        PollingEnabledPaymentFlow._instance?.stop();
        PollingEnabledPaymentFlow._instance = this;

        this._running = true;
        return this._pollTxnStatus(props);
    }

    getOrderId(): string {
        if (this._orderData !== null) {
            return String(this._orderData.orderId);
        }

        if (this._transactionData !== null) {
            return String(this._transactionData.paymentTxnId);
        }

        return "";
    }

    getPaymentTxnId(): string {
        if (!isEmpty(this._orderData)) {
            return this._orderData.paymentPartnerOrderId || this._orderData.paymentTxnId;
        }

        if (!isEmpty(this._transactionData)) {
            return this._transactionData.paymentTxnId;
        }

        return "";
    }

    private async _checkTxnStatus(paymentCode: string): Promise<boolean> {
        const passId = this.getPaymentTxnId();

        if (isEmpty(passId)) return false;

        try {
            const response = await checkTransactionStatusPublic(this._apiHost, passId, paymentCode);
            const status = response?.toUpperCase() || "";

            if (status.indexOf(POLLING_TXN_STATUS.SUCCESS) > -1 || status.indexOf(POLLING_TXN_STATUS.FAILED) > -1) {
                return true;
            }
        } catch (err) {
            // no catch
        }

        return false;
    }

    private async _pollTxnStatus(props: CheckTxnStatusProps): Promise<boolean> {
        if (!this._running) return false;

        // Defer the very first network call by one tick to allow mount/unmount
        // stabilization (e.g., React StrictMode double-invocation on initial render).
        // If the flow is stopped during this defer (cleanup), we avoid issuing
        // the first vpa-txn status request twice.
        await this._wait(0);
        if (!this._running) return false;

        const _startTime = props.startTime || Date.now();
        const _endTime = _startTime + props.duration;
        let callCount = 0;
        while (Date.now() < _endTime && this._running) {
            callCount++;
            const result = await this._checkTxnStatus(props.paymentCode);
            if (result) return true;
            await this._wait(props.delay);
        }

        // (3.3) Polling duration exhausted without a terminal status. Guarded so it fires only on a
        // genuine timeout, not when stop() ended the loop early.
        if (this._running && Date.now() >= _endTime) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_PENNY_TXN_CONFIRM_ORDER,
                state: WP_PENNY_TXN_CONFIRM_ORDER_STATE.PENNY_TXN_POLLING_TIMEOUT,
                orderId: this.getOrderId(),
                paymentCode: props.paymentCode,
                durationMs: props.duration,
                callCount,
            });
        }

        return false;
    }

    private _wait(time: number): Promise<void> {
        return new Promise((resolve, _) => setTimeout(resolve, time));
    }
}
